/** @format */

import { useFormik } from "formik";
import React, { type FC, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import * as yup from "yup";
import theme from "../../../styles/theme";
import Box from "../../atoms/box/Box";
import Button from "../../atoms/buttons/Button";
import Typography from "../../atoms/typography/Typography";
import Form, { type FormProps } from "../../core/inputs/Form";
import useDataPrivacyShield from "../../special/auth/useDataPrivacyShield";
import type { AuthEmailProps } from "./AuthEmail.types";

const AuthEmail: FC<AuthEmailProps> = ({
  variant,
  authHandler,
  setPage,
  errorMessage,
  formikStore: initial,
  validationSchema,
  project,
}) => {
  const { t } = useTranslation();
  const { handleDataPrivacy } = useDataPrivacyShield(project);

  const [variantState, setVariantState] = useState("login");

  useEffect(() => {
    if (variant === "register") {
      setVariantState("register");
    }
  }, [variant]);

  const formik = useFormik({
    initialValues: initial?.values,
    validationSchema,
    onSubmit: () => {},
  });
  useEffect(() => {
    initial.setValues(formik?.values);
  }, [formik?.values]);

  const handleSubmit = (e?) => {
    e?.preventDefault();

    if (variantState === "register") return authHandler.createUser(formik);

    authHandler.signIn.email(formik);
  };

  const inputItemsLogin: FormProps["inputItems"] = [
    // {
    //   name: "email",
    //   type: "email",
    //   placeholder: "E-Mail",
    //   autoComplete: "email",
    // },
    {
      name: "password",
      type: "password",
      placeholder: t("auth:password"),
      autoComplete: "current-password",
      autoFocus: true,
      onSubmit: () => handleSubmit(),
    },
  ];

  const inputItemsRegister: FormProps["inputItems"] = [
    { name: "email", type: "email", placeholder: t("auth:email") },
    {
      name: "password",
      type: "password",
      placeholder: t("auth:password"),
    },
    {
      name: "confirmPassword",
      type: "password",
      placeholder: t("auth:confirmPassword"),
    },
    {
      name: "handle",
      type: "text",
      placeholder: t("global:username"),
    },
  ];

  return (
    <Box
      flexDirection="column"
      margin="150px 10% 50px 10%"
      position="relative"
      zIndex={9999}
    >
      <Typography variant="h1" style={{ position: "relative" }}>
        {t("auth:authOptions.titleLine1")}
      </Typography>
      <Typography variant="h1" style={{ position: "relative" }}>
        {t("auth:authOptions.titleLine2")}
      </Typography>
      <Box margin="25px 0px 24px 0px" alignItems="center" gap="5px">
        {/* {variantState === "login" ? (
          <React.Fragment>
            <Typography variant="bodyBg" style={{ position: "relative" }}>
              {t("auth_login_user_redirection_question")}
            </Typography>{" "}
            <Button
              variant="tertiary"
              text={t("register_now")}
              onClick={() => setVariantState("register")}
            />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Typography variant="bodyBg" style={{ position: "relative" }}>
              {t("auth_register_user_redirection_question")}
            </Typography>
            <Button
              variant="tertiary"
              text={t("login_now")}
              onClick={() => setVariantState("login")}
            />
          </React.Fragment>
        )} */}
      </Box>
      <Box gap="16px" flexDirection="column">
        <Form
          inputItems={
            variantState === "register" ? inputItemsRegister : inputItemsLogin
          }
          formik={formik}
          classNames={{
            input: "bg-white",
          }}
        />

        {errorMessage && (
          <Typography variant="bodySm" color={theme.colors.signal.redDark}>
            {t(errorMessage)}
          </Typography>
        )}
      </Box>
      <Box
        flexDirection="row"
        gap="5px"
        alignItems="center"
        margin="14px 0 36px 0"
      >
        {variantState === "login" ? (
          <React.Fragment>
            <p style={{ position: "relative" }}>
              {t("auth:authOptions.forgotPassword")}
            </p>{" "}
            <Button
              variant="tertiary"
              size="sm"
              text={t("global:reset")}
              weight="bold"
              onClick={() => setPage?.("authResetPassword")}
            />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Typography variant="bodySm" style={{ position: "relative" }}>
              <Trans i18nKey="auth:terms">
                ...
                <Link
                  to={`/projects/${project?.projectId}/terms`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", fontWeight: "500" }}
                />
                ...
                <Link
                  to={`/projects/${project?.projectId}/privacyPolicy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", fontWeight: "500" }}
                />
                ...
              </Trans>
            </Typography>
          </React.Fragment>
        )}
      </Box>
      <Button
        variant="white"
        width="max"
        text={
          variantState === "register" ? t("auth:register") : t("auth:login")
        }
        loading={authHandler?.loading?.email}
        onClick={async () => {
          const privacyAccepted = await handleDataPrivacy();
          if (privacyAccepted) {
            handleSubmit();
          }
        }}
        disabled={!initial?.isValid}
        data-cy="email-login-register-submit"
      />
    </Box>
  );
};

export default AuthEmail;
